import { Transition } from "@headlessui/react";
import { useBodyClass } from "@portal/hooks/useBodyClass";
import { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import FocusLock from "react-focus-lock";
import { cn } from "../lib/utils";

export const ModalPortal = ({ children }: { children: React.ReactNode }) => {
	const container = useMemo(() => document.createElement("div"), []);

	useEffect(() => {
		document.body.appendChild(container);
		return () => {
			// Clean up the DOM node manually
			document.body.removeChild(container);
		};
	}, [container]);

	return createPortal(children, container);
};

let modalCount = 0;
const openModalOrders = new Set<number>();

type ModalProps = {
	onClickOutside?: () => void;
	/** Also fires `onClickOutside` on Escape, for the topmost open modal only. Off by default so forms keep typed input. */
	closeOnEscape?: boolean;
	children: ReactNode;
	itemClassName?: string;
	autoFocus?: boolean;
	/** Moves focus back to whatever opened the modal, such as a table row, when it closes. */
	returnFocus?: boolean;
};

export const Modal = ({
	onClickOutside,
	closeOnEscape = false,
	children,
	itemClassName,
	autoFocus = false,
	returnFocus = false,
}: ModalProps) => {
	const handleClickOutside = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onClickOutside && onClickOutside();
		},
		[onClickOutside],
	);
	const handleFocusLockClick = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
	}, []);

	const [order] = useState(() => ++modalCount);

	useEffect(() => {
		openModalOrders.add(order);
		return () => {
			openModalOrders.delete(order);
		};
	}, [order]);

	useEffect(() => {
		if (!closeOnEscape || !onClickOutside) return;
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "Escape" || event.defaultPrevented || event.isComposing) return;
			if (order !== Math.max(...openModalOrders)) return;
			onClickOutside();
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [order, closeOnEscape, onClickOutside]);

	// This is a work around to prevent sticky elements with any non-zero z-index from improperly showing above the modal.
	// this is accompanied by a [.modal-active_&]:z-0 class on the sticky element to ensure it goes below the modal
	useBodyClass("modal-active");

	return (
		<ModalPortal>
			<div
				data-testid="modal-backdrop"
				onMouseDown={handleClickOutside}
				onClick={(e) => e.stopPropagation()}
				className="absolute bg-gray-950/30 top-0 right-0 bottom-0 left-0 flex flex-col items-center justify-center z-[200000] overflow-auto"
			>
				<Transition
					appear={true}
					show={true}
					as={Fragment}
					enter="transition-opacity ease-in duration-[90ms]"
					enterFrom="opacity-50"
					enterTo="opacity-100"
					leave="transition-opacity ease-in duration-75"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<FocusLock autoFocus={autoFocus} returnFocus={returnFocus}>
						<div
							onMouseDown={handleFocusLockClick}
							onMouseUp={handleFocusLockClick}
							onClick={handleFocusLockClick}
							className={cn("relative", itemClassName)}
						>
							{children}
						</div>
					</FocusLock>
				</Transition>
				<div className="h-[5%] w-full"></div>
			</div>
		</ModalPortal>
	);
};
